import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";

// const API_BASE = "https://hopeful-cosmic-koala.ngrok-free.app/api";
const API_BASE = "/api";

// ── Helpers ────────────────────────────────────────────────────────────────
const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

const extractPlainText = (descriptionJson) => {
  try {
    const parsed = JSON.parse(descriptionJson);
    return parsed.ops.map((op) => op.insert).join("").replace(/\n+/g, " ").trim();
  } catch {
    return "";
  }
};

const readTime = (text) =>
  `${Math.max(1, Math.ceil((text || "").split(" ").length / 200))} min read`;

// Renders Quill Delta JSON as React <p> elements
const renderQuillContent = (descriptionJson) => {
  try {
    const parsed = JSON.parse(descriptionJson);
    const text = parsed.ops
      .map((op) => op.insert)
      .join("")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    return text
      .split("\n\n")
      .filter(Boolean)
      .map((para, i) => <p key={i}>{para}</p>);
  } catch {
    return <p>Content unavailable.</p>;
  }
};

// ── Inline SVG Icons ───────────────────────────────────────────────────────
const LinkedInIcon = () => (
  <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);
const TwitterIcon = () => (
  <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);
const WhatsAppIcon = () => (
  <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);
const YouTubeIcon = () => (
  <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);
const ArrowIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M19 12H5M12 5l-7 7 7 7"/>
  </svg>
);
const CalendarIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
  </svg>
);
const ClockIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
  </svg>
);

// ── Component ──────────────────────────────────────────────────────────────
const BlogDetail = () => {
  const { slug } = useParams();

  const [blog, setBlog]         = useState(null);
  const [related, setRelated]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [scrolled, setScrolled] = useState(false);

  // Fetch blog detail
  useEffect(() => {
    const fetchBlog = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE}/blogs/${slug}/`, {
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "69420",
          },
        });

        if (!response.ok) throw new Error(`Failed to fetch blog: ${response.status}`);
        const data = await response.json();
        setBlog(data);

        // Fetch related posts via first tag
        if (data.tags?.length) {
          const relRes = await fetch(
            `${API_BASE}/blogs/?tag=${data.tags[0].slug}&page_size=4`,
            { headers: { "ngrok-skip-browser-warning": "69420" } }
          );
          if (relRes.ok) {
            const relData = await relRes.json();
            const results = Array.isArray(relData) ? relData : (relData.results ?? []);
            setRelated(results.filter((b) => b.slug !== slug).slice(0, 3));
          }
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug]);

  // Scroll listener for sticky topbar
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // ── Loading state ────────────────────────────────────────────────────────
  if (loading) return (
    <div className="bd-root" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
      <div style={{ width: 44, height: 44, border: "4px solid #e8f4fd", borderTopColor: "#0b1c6b", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  // ── Error state ──────────────────────────────────────────────────────────
  if (error || !blog) return (
    <div className="bd-root" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: "3rem" }}>⚠️</div>
      <p style={{ color: "rgba(255,255,255,0.7)", fontFamily: "sans-serif" }}>
        {error || "Article not found."}
      </p>
      <Link to="/blogs" style={{ color: "#159EEC", fontSize: "0.9rem" }}>← Back to Blog</Link>
    </div>
  );

  const plainText = extractPlainText(blog.descriptionJson);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');

        :root {
          --navy:      #0b1c6b;
          --royal:     #1a3aa1;
          --sky:       #159EEC;
          --sky-light: #3db8ff;
          --sky-pale:  #e6f6ff;
          --white:     #ffffff;
          --off:       #f0f6ff;
          --muted:     rgba(255,255,255,0.55);
          --border:    rgba(21,158,236,0.18);
          --glow:      rgba(21,158,236,0.25);
          --card-bg:   rgba(255,255,255,0.04);
        }

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        body {
          font-family: 'DM Sans', sans-serif;
          background: var(--navy);
          color: var(--white);
          min-height: 100vh;
          overflow-x: hidden;
        }

        .bd-root {
          position: relative;
          background:
            radial-gradient(ellipse 80% 60% at 10% 0%, rgba(21,158,236,0.14) 0%, transparent 60%),
            radial-gradient(ellipse 60% 50% at 90% 100%, rgba(26,58,161,0.5) 0%, transparent 55%),
            radial-gradient(ellipse 40% 40% at 60% 40%, rgba(21,158,236,0.07) 0%, transparent 50%),
            var(--navy);
          min-height: 100vh;
        }

        .bd-topbar {
          position: sticky;
          top: 0;
          z-index: 100;
          padding: 14px 32px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          transition: background 0.3s, backdrop-filter 0.3s, border-bottom 0.3s;
          border-bottom: 1px solid transparent;
        }
        .bd-topbar.scrolled {
          background: rgba(11,28,107,0.82);
          backdrop-filter: blur(16px);
          border-bottom: 1px solid var(--border);
        }
        .bd-back-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(21,158,236,0.12);
          border: 1px solid var(--border);
          color: var(--sky);
          text-decoration: none;
          font-size: 0.82rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          padding: 8px 18px;
          border-radius: 100px;
          transition: all 0.25s;
        }
        .bd-back-btn:hover {
          background: var(--sky);
          color: var(--navy);
          border-color: var(--sky);
          transform: translateX(-3px);
        }
        .bd-topbar-brand {
          font-family: 'Playfair Display', serif;
          font-size: 0.95rem;
          color: var(--muted);
          letter-spacing: 0.02em;
        }

        .bd-hero {
          position: relative;
          min-height: 72vh;
          display: flex;
          align-items: flex-end;
          overflow: hidden;
        }
        .bd-hero-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          opacity: 0.32;
          transform: scale(1.04);
          animation: heroZoom 18s ease-in-out infinite alternate;
          z-index: 0;
        }
        @keyframes heroZoom {
          from { transform: scale(1.04); }
          to   { transform: scale(1.12); }
        }
        .bd-hero-placeholder {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, var(--royal) 0%, var(--navy) 100%);
          display: flex; align-items: center; justify-content: center;
          font-size: 6rem;
          opacity: 0.3;
          z-index: 0;
        }
        .bd-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, var(--navy) 0%, rgba(11,28,107,0.6) 45%, transparent 100%);
          z-index: 1;
        }
        .bd-hero-content {
          position: relative;
          z-index: 2;
          padding: 0 clamp(20px,5vw,80px) clamp(40px,6vw,72px);
          max-width: 900px;
          animation: fadeUp 0.8s ease both;
        }
        .bd-tags-row { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 20px; }
        .bd-tag-pill {
          background: rgba(21,158,236,0.18);
          border: 1px solid rgba(21,158,236,0.4);
          color: var(--sky-light);
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          padding: 5px 14px;
          border-radius: 100px;
          backdrop-filter: blur(8px);
        }
        .bd-hero-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2rem,5vw,3.6rem);
          font-weight: 900;
          line-height: 1.12;
          color: var(--white);
          margin-bottom: 24px;
          text-shadow: 0 2px 40px rgba(0,0,0,0.5);
        }
        .bd-meta-row { display: flex; align-items: center; gap: 24px; flex-wrap: wrap; }
        .bd-meta-item { display: flex; align-items: center; gap: 7px; font-size: 0.83rem; color: var(--muted); font-weight: 500; }
        .bd-meta-item svg { color: var(--sky); }
        .bd-meta-divider { width: 4px; height: 4px; border-radius: 50%; background: var(--border); }

        .bd-layout {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: 1fr 320px;
          gap: 40px;
          max-width: 1240px;
          margin: 0 auto;
          padding: clamp(40px,5vw,72px) clamp(20px,4vw,48px);
        }
        @media (max-width: 900px) {
          .bd-layout { grid-template-columns: 1fr; }
          .bd-sidebar { display: none; }
        }

        .bd-article {
          background: var(--card-bg);
          border: 1px solid var(--border);
          border-radius: 20px;
          padding: clamp(28px,4vw,52px);
          backdrop-filter: blur(12px);
          position: relative;
          overflow: hidden;
          animation: fadeUp 0.6s ease both 0.1s;
        }
        .bd-article::before {
          content: '';
          position: absolute;
          top: -1px; left: 0; right: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--sky), var(--royal), var(--sky));
          background-size: 200% 100%;
          animation: shimmerBar 4s linear infinite;
        }
        @keyframes shimmerBar {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .bd-article p {
          font-size: clamp(1rem,1.5vw,1.08rem);
          line-height: 1.88;
          color: rgba(255,255,255,0.85);
          margin-bottom: 1.6em;
          font-weight: 300;
        }
        .bd-article p:first-child {
          font-size: clamp(1.08rem,1.8vw,1.2rem);
          color: rgba(255,255,255,0.95);
          font-weight: 400;
          font-family: 'Playfair Display', serif;
        }
        .bd-article p:last-child { margin-bottom: 0; }
        .bd-article p:first-child::first-letter {
          float: left;
          font-size: 4.2em;
          line-height: 0.75;
          margin: 8px 12px 0 0;
          color: var(--sky);
          font-family: 'Playfair Display', serif;
          font-weight: 900;
        }

        .bd-share-strip {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          margin-top: 40px;
          padding-top: 28px;
          border-top: 1px solid var(--border);
        }
        .bd-share-label { font-size: 0.75rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--muted); margin-right: 4px; }
        .bd-share-btn { display: inline-flex; align-items: center; gap: 7px; padding: 8px 16px; border-radius: 100px; font-size: 0.8rem; font-weight: 600; text-decoration: none; transition: all 0.22s; border: 1px solid transparent; }
        .bd-share-li  { background: rgba(0,119,181,0.15); border-color: rgba(0,119,181,0.35); color: #5cb8e4; }
        .bd-share-tw  { background: rgba(29,161,242,0.12); border-color: rgba(29,161,242,0.3);  color: #5cb8e4; }
        .bd-share-wa  { background: rgba(37,211,102,0.12); border-color: rgba(37,211,102,0.3);  color: #5ecf87; }
        .bd-share-yt  { background: rgba(255,0,0,0.10);    border-color: rgba(255,0,0,0.25);    color: #ff7070; }
        .bd-share-btn:hover { transform: translateY(-2px); filter: brightness(1.25); }

        .bd-sidebar { display: flex; flex-direction: column; gap: 24px; animation: fadeUp 0.6s ease both 0.25s; }
        .bd-sidebar-card { background: var(--card-bg); border: 1px solid var(--border); border-radius: 16px; padding: 24px; backdrop-filter: blur(10px); }
        .bd-sidebar-title { font-family: 'Playfair Display', serif; font-size: 1rem; font-weight: 700; color: var(--sky-light); margin-bottom: 18px; padding-bottom: 12px; border-bottom: 1px solid var(--border); display: flex; align-items: center; gap: 8px; }
        .bd-sidebar-title::before { content: ''; display: inline-block; width: 3px; height: 16px; background: linear-gradient(180deg, var(--sky), var(--royal)); border-radius: 2px; }

        .bd-related-item { display: flex; gap: 12px; align-items: flex-start; padding: 12px 0; border-bottom: 1px solid var(--border); cursor: pointer; transition: all 0.2s; text-decoration: none; background: none; border-left: none; border-right: none; width: 100%; text-align: left; }
        .bd-related-item:first-of-type { padding-top: 0; }
        .bd-related-item:last-child { border-bottom: none; padding-bottom: 0; }
        .bd-related-item:hover .bd-related-item-title { color: var(--sky); }
        .bd-related-thumb { width: 54px; height: 54px; border-radius: 10px; background: linear-gradient(135deg, var(--royal), var(--sky)); flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; overflow: hidden; }
        .bd-related-thumb img { width: 100%; height: 100%; object-fit: cover; }
        .bd-related-item-title { font-size: 0.83rem; font-weight: 500; color: rgba(255,255,255,0.82); line-height: 1.4; transition: color 0.2s; }
        .bd-related-item-date { font-size: 0.72rem; color: var(--muted); margin-top: 5px; }

        .bd-tag-cloud { display: flex; flex-wrap: wrap; gap: 8px; }
        .bd-tag-cloud-item { background: rgba(21,158,236,0.1); border: 1px solid var(--border); color: var(--sky-light); font-size: 0.75rem; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; padding: 6px 14px; border-radius: 100px; cursor: pointer; transition: all 0.2s; }
        .bd-tag-cloud-item:hover { background: var(--sky); color: var(--navy); border-color: var(--sky); }

        .bd-social-section {
          position: relative;
          z-index: 1;
          margin: 0 clamp(20px,4vw,48px) clamp(40px,6vw,80px);
          background: linear-gradient(135deg, rgba(21,158,236,0.12) 0%, rgba(26,58,161,0.2) 100%);
          border: 1px solid rgba(21,158,236,0.25);
          border-radius: 24px;
          padding: clamp(36px,5vw,60px);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 32px;
          flex-wrap: wrap;
          backdrop-filter: blur(10px);
          overflow: hidden;
        }
        .bd-social-section::after { content: '✦'; position: absolute; right: 40px; top: 50%; transform: translateY(-50%); font-size: 8rem; color: rgba(21,158,236,0.06); pointer-events: none; line-height: 1; }
        .bd-social-left h2 { font-family: 'Playfair Display', serif; font-size: clamp(1.4rem,3vw,2rem); font-weight: 900; color: var(--white); margin-bottom: 8px; }
        .bd-social-left p { font-size: 0.9rem; color: var(--muted); max-width: 360px; line-height: 1.6; }
        .bd-social-links { display: flex; gap: 12px; flex-wrap: wrap; }
        .bd-social-link { display: inline-flex; align-items: center; gap: 9px; padding: 11px 22px; border-radius: 100px; font-size: 0.83rem; font-weight: 600; text-decoration: none; transition: all 0.25s; border: 1px solid var(--border); color: var(--white); background: rgba(255,255,255,0.06); backdrop-filter: blur(8px); }
        .bd-social-link:hover { background: var(--sky); border-color: var(--sky); color: var(--navy); transform: translateY(-3px); box-shadow: 0 8px 24px rgba(21,158,236,0.35); }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="bd-root">

        {/* Sticky topbar */}
        <nav className={`bd-topbar${scrolled ? " scrolled" : ""}`}>
          <Link to="/blogs" className="bd-back-btn">
            <ArrowIcon /> Back to News
          </Link>
          <span className="bd-topbar-brand">GCCI</span>
        </nav>

        {/* Hero */}
        <div className="bd-hero">
          {blog.cover_image
            ? <img className="bd-hero-img" src={blog.cover_image} alt={blog.title} onError={(e) => { e.target.style.display = "none"; }} />
            : <div className="bd-hero-placeholder">🙏</div>
          }
          <div className="bd-hero-overlay" />
          <div className="bd-hero-content">
            <div className="bd-tags-row">
              {(blog.tags || []).map((t) => (
                <span key={t.slug} className="bd-tag-pill">{t.title}</span>
              ))}
            </div>
            <h1 className="bd-hero-title">{blog.title}</h1>
            <div className="bd-meta-row">
              <span className="bd-meta-item">
                <CalendarIcon /> {formatDate(blog.published_at)}
              </span>
              <span className="bd-meta-divider" />
              <span className="bd-meta-item">
                <ClockIcon /> {readTime(plainText)}
              </span>
            </div>
          </div>
        </div>

        {/* Main layout */}
        <div className="bd-layout">

          {/* Article */}
          <article className="bd-article">
            {renderQuillContent(blog.descriptionJson)}

            {/* Share strip */}
            <div className="bd-share-strip">
              <span className="bd-share-label">Share</span>
              <a href="https://www.linkedin.com/company/gospel-city-church-international/" target="_blank" rel="noopener noreferrer" className="bd-share-btn bd-share-li">
                <LinkedInIcon /> LinkedIn
              </a>
              <a href="https://groups.imo.im/.5l9akqTOrYa5Tv4T4" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
            <i className="bi bi-chat-dots fs-4"></i>
            </a>
              <a href="https://x.com/GospelCityChur" target="_blank" rel="noopener noreferrer" className="bd-share-btn bd-share-tw">
                <TwitterIcon /> Twitter
              </a>
              <a href="https://whatsapp.com/channel/0029Vb769UyDzgTBAZZcb40F" target="_blank" rel="noopener noreferrer" className="bd-share-btn bd-share-wa">
                <WhatsAppIcon /> WhatsApp
              </a>
              <a href="https://youtube.com/@gospelcitychurchinternational" target="_blank" rel="noopener noreferrer" className="bd-share-btn bd-share-yt">
                <YouTubeIcon /> YouTube
              </a>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="bd-sidebar">

            {/* Related posts */}
            {related.length > 0 && (
              <div className="bd-sidebar-card">
                <div className="bd-sidebar-title">Related Articles</div>
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/blogs/${r.slug}`}
                    className="bd-related-item"
                  >
                    <div className="bd-related-thumb">
                      {r.cover_image
                        ? <img src={r.cover_image} alt={r.title} onError={(e) => { e.target.parentNode.textContent = "🙏"; }} />
                        : "🙏"
                      }
                    </div>
                    <div>
                      <div className="bd-related-item-title">{r.title}</div>
                      <div className="bd-related-item-date">{formatDate(r.published_at)}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Tags */}
            <div className="bd-sidebar-card">
              <div className="bd-sidebar-title">Topics</div>
              <div className="bd-tag-cloud">
                {(blog.tags || []).map((t) => (
                  <span key={t.slug} className="bd-tag-cloud-item">{t.title}</span>
                ))}
              </div>
            </div>

            {/* About */}
            <div className="bd-sidebar-card">
              <div className="bd-sidebar-title">About GCCI</div>
              <p style={{ fontSize: "0.82rem", lineHeight: 1.7, color: "rgba(255,255,255,0.65)", fontWeight: 300 }}>
                Gospel City Church International is a vibrant, Spirit-led community committed to transforming lives through faith, worship, and discipleship.
              </p>
            </div>

          </aside>
        </div>

        {/* Social CTA */}
        <section className="bd-social-section">
          <div className="bd-social-left">
            <h2>Stay Connected</h2>
            <p>Follow Gospel City Church International for the latest news, devotionals, and updates across all platforms.</p>
          </div>
          <div className="bd-social-links">
            <a href="https://www.linkedin.com/company/gospel-city-church-international/" target="_blank" rel="noopener noreferrer" className="bd-social-link">
              <LinkedInIcon /> LinkedIn
            </a>
            <a href="https://x.com/GospelCityChur" target="_blank" rel="noopener noreferrer" className="bd-social-link">
              <TwitterIcon /> Twitter / X
            </a>
            <a href="https://whatsapp.com/channel/0029Vb769UyDzgTBAZZcb40F" target="_blank" rel="noopener noreferrer" className="bd-social-link">
              <WhatsAppIcon /> WhatsApp
            </a>
            <a href="https://youtube.com/@gospelcitychurchinternational" target="_blank" rel="noopener noreferrer" className="bd-social-link">
              <YouTubeIcon /> YouTube
            </a>
          </div>
        </section>

      </div>
    </>
  );
};

export default BlogDetail;